
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import IconFilter from "@/components/common/icon/IconFilter.vue";
import Multiselect from "vue-multiselect";
import { mapGetters } from "vuex";
import { CashOrderApprovalStatusEnum, IApprovalsFilterModel } from "@/entities";

@Component({
  name: "approvals-filter",
  computed: {
    ...mapGetters(["getLocale"]),
  },
  components: {
    IconFilter,
    Multiselect,
  },
})
export default class ApprovalsFilter extends Vue {
  @Prop({ required: true }) params!: IApprovalsFilterModel;

  visible = false;
  paramsEdit: IApprovalsFilterModel = { ...this.params };

  private static readonly MAX_RANGE_DAYS = 14;

  get locale(): string {
    return (this as any).getLocale || "en";
  }

  get statusOptions(): Array<{ code: CashOrderApprovalStatusEnum; desc: string }> {
    return [
      {
        code: CashOrderApprovalStatusEnum.PENDING,
        desc: this.$t("label.pending_approval"),
      },
      {
        code: CashOrderApprovalStatusEnum.REJECTED,
        desc: this.$t("label.rejected_approval"),
      },
      {
        code: CashOrderApprovalStatusEnum.APPROVED,
        desc: this.$t("label.approved_approval"),
      },
      {
        code: CashOrderApprovalStatusEnum.EXPIRED,
        desc: this.$t("label.expired_approval"),
      },
    ];
  }

  get statusSelected(): Array<{ code: CashOrderApprovalStatusEnum; desc: string }> {
    const selected = new Set(this.paramsEdit.approvalStatus || []);
    return this.statusOptions.filter((o) => selected.has(o.code));
  }

  set statusSelected(value: Array<{ code: CashOrderApprovalStatusEnum; desc: string }>) {
    this.paramsEdit.approvalStatus = (value || []).map((v) => v.code);
  }

  get orderDateFrom(): string {
    return this.paramsEdit.orderDateFrom ? this.toYYYYMMDD(this.paramsEdit.orderDateFrom) : "";
  }

  set orderDateFrom(value: string) {
    this.paramsEdit.orderDateFrom = value
      ? new Date(`${value}T00:00:00`)
      : this.paramsEdit.orderDateFrom;
  }

  get orderDateTo(): string {
    return this.paramsEdit.orderDateTo ? this.toYYYYMMDD(this.paramsEdit.orderDateTo) : "";
  }

  set orderDateTo(value: string) {
    this.paramsEdit.orderDateTo = value
      ? new Date(`${value}T00:00:00`)
      : this.paramsEdit.orderDateTo;
  }

  get filterLength(): number {
    let length = 0;
    if (this.params.approvalStatus?.length) length++;
    if (this.params.username) length++;
    if (this.params.serviceCode) length++;
    if (this.params.orderDateFrom) length++;
    if (this.params.orderDateTo) length++;
    return length;
  }

  get orderDateFromMin(): string {
    if (!this.paramsEdit.orderDateTo) return "";
    const to = this.startOfDay(this.paramsEdit.orderDateTo);
    return this.toYYYYMMDD(this.addDays(to, -ApprovalsFilter.MAX_RANGE_DAYS));
  }

  get orderDateFromMax(): string {
    return this.orderDateTo || "";
  }

  get orderDateToMin(): string {
    return this.orderDateFrom || "";
  }

  get orderDateToMax(): string {
    if (!this.paramsEdit.orderDateFrom) return "";
    const from = this.startOfDay(this.paramsEdit.orderDateFrom);
    return this.toYYYYMMDD(this.addDays(from, ApprovalsFilter.MAX_RANGE_DAYS));
  }

  @Watch("paramsEdit.orderDateFrom", { deep: false })
  onOrderDateFromChanged(newVal: Date): void {
    if (!newVal) return;

    const from = this.startOfDay(newVal);
    if (this.paramsEdit.orderDateFrom.getTime() !== from.getTime()) {
      this.paramsEdit.orderDateFrom = from;
    }

    const maxToAllowed = this.addDays(from, ApprovalsFilter.MAX_RANGE_DAYS);

    const currentTo = this.startOfDay(this.paramsEdit.orderDateTo);

    if (currentTo.getTime() < from.getTime()) {
      this.paramsEdit.orderDateTo = new Date(from);
      return;
    }

    if (currentTo.getTime() > maxToAllowed.getTime()) {
      this.paramsEdit.orderDateTo = new Date(maxToAllowed);
    }
  }

  @Watch("paramsEdit.orderDateTo", { deep: false })
  onOrderDateToChanged(newVal: Date): void {
    if (!newVal) return;

    const to = this.startOfDay(newVal);
    if (this.paramsEdit.orderDateTo.getTime() !== to.getTime()) {
      this.paramsEdit.orderDateTo = to;
    }

    const from = this.startOfDay(this.paramsEdit.orderDateFrom);

    if (to.getTime() < from.getTime()) {
      this.paramsEdit.orderDateFrom = new Date(to);
      return;
    }

    const minFromAllowed = this.addDays(to, -ApprovalsFilter.MAX_RANGE_DAYS);
    if (from.getTime() < minFromAllowed.getTime()) {
      this.paramsEdit.orderDateFrom = new Date(minFromAllowed);
    }
  }

  private startOfDay(d: Date): Date {
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private addDays(d: Date, days: number): Date {
    const date = new Date(d);
    date.setDate(date.getDate() + days);
    return date;
  }

  doCleanUp(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const from = new Date(today);
    from.setDate(from.getDate() - 7);

    this.paramsEdit = {
      approvalStatus: undefined,
      username: undefined,
      orderDateFrom: from,
      orderDateTo: today,
    };

    this.$emit("update:params", { ...this.paramsEdit });
    this.visible = false;
    this.$emit("filter");
  }

  doFilter(): void {
    this.$emit("update:params", { ...this.paramsEdit });
    this.visible = false;
    this.$emit("filter");
  }

  private toYYYYMMDD(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
}
